import { type Dtos, type FinancialPeriod } from "@inrev/common";
import { patchLineItemGroupView, renameLineItemGroupViewLabel } from "./applyFinancialViewValues";

const mapBalanceSheetGroup = (
	view: FinancialPeriod.BalanceSheetView,
	sectionPath: string,
	mapGroup: (group: FinancialPeriod.LineItemGroupView) => FinancialPeriod.LineItemGroupView,
): FinancialPeriod.BalanceSheetView => {
	const parts = sectionPath.split(".");

	if (parts[0] === "equity" && parts.length === 2) {
		const key = parts[1] as FinancialPeriod.EquityKey;
		return {
			...view,
			equity: { ...view.equity, [key]: mapGroup(view.equity[key]) },
		};
	}

	if (parts[0] === "assets" && parts.length === 3) {
		if (parts[1] === "current") {
			const key = parts[2] as FinancialPeriod.CurrentAssetKey;
			return {
				...view,
				assets: {
					...view.assets,
					current: { ...view.assets.current, [key]: mapGroup(view.assets.current[key]) },
				},
			};
		}
		if (parts[1] === "fixed") {
			const key = parts[2] as FinancialPeriod.FixedAssetKey;
			return {
				...view,
				assets: {
					...view.assets,
					fixed: { ...view.assets.fixed, [key]: mapGroup(view.assets.fixed[key]) },
				},
			};
		}
		if (parts[1] === "nonCurrent") {
			const key = parts[2] as FinancialPeriod.NonCurrentAssetKey;
			return {
				...view,
				assets: {
					...view.assets,
					nonCurrent: { ...view.assets.nonCurrent, [key]: mapGroup(view.assets.nonCurrent[key]) },
				},
			};
		}
	}

	if (parts[0] === "liabilities" && parts.length === 3) {
		if (parts[1] === "current") {
			const key = parts[2] as FinancialPeriod.CurrentLiabilityKey;
			return {
				...view,
				liabilities: {
					...view.liabilities,
					current: { ...view.liabilities.current, [key]: mapGroup(view.liabilities.current[key]) },
				},
			};
		}
		if (parts[1] === "nonCurrent") {
			const key = parts[2] as FinancialPeriod.NonCurrentLiabilityKey;
			return {
				...view,
				liabilities: {
					...view.liabilities,
					nonCurrent: {
						...view.liabilities.nonCurrent,
						[key]: mapGroup(view.liabilities.nonCurrent[key]),
					},
				},
			};
		}
	}

	return view;
};

export const patchBalanceSheetView = (
	view: FinancialPeriod.BalanceSheetView,
	body: Dtos.Admin.BalanceSheet.Update.Request,
): FinancialPeriod.BalanceSheetView => {
	const { sectionPath, label, values } = body;
	return mapBalanceSheetGroup(view, sectionPath, (group) =>
		patchLineItemGroupView(group, label, values),
	);
};

export const renameBalanceSheetViewLabel = (
	view: FinancialPeriod.BalanceSheetView,
	sectionPath: string,
	oldLabel: string,
	newLabel: string,
): FinancialPeriod.BalanceSheetView =>
	mapBalanceSheetGroup(view, sectionPath, (group) =>
		renameLineItemGroupViewLabel(group, oldLabel, newLabel),
	);
